import type { AppChartDataItem, AppChartSummary } from "@/features/app-studio/api/appChartsApi";
import { isAppChartDataItem } from "@/features/app-studio/api/appChartsApi";
import type {
  AppKpiDataItem,
  AppKpiDrilldownItemType,
  AppKpiFormat,
  AppKpiSummary,
} from "@/features/app-studio/api/appKpisApi";
import { isAppKpiDataItem } from "@/features/app-studio/api/appKpisApi";
import {
  isAppNuggetFeedDataItem,
  normalizeAppNuggetFeedDataItem,
  type AppNuggetFeedDataItem,
} from "@/features/app-studio/api/appNuggetFeedsApi";
import type { AppQuerySummary } from "@/features/app-studio/api/appQueriesApi";
import type { AppQueryBuilderCompileRequest } from "@/features/app-studio/api/appQueryBuilderApi";
import type {
  AppTableDataItem,
  AppTableDisplayConfig,
  AppTableSummary,
} from "@/features/app-studio/api/appTablesApi";
import { isAppTableDataItem } from "@/features/app-studio/api/appTablesApi";
import {
  isAppTextWidgetDataItem,
  type AppTextWidgetDataItem,
} from "@/features/app-studio/api/appTextWidgetsApi";
import type { AppWidgetInputMode } from "@/features/app-studio/lib/appChartLayout";
import type { AppTabFilterRequest } from "@/features/app-studio/model/appTabFilters";
import type { ChartType, GeneralizedChartDefinition } from "@/features/chat/model/chatTypes";
import { apiFetch } from "@/shared/api/apiFetch";
import { readApiErrorMessage } from "@/shared/api/parseApiErrorMessage";

export type AppItemType = "chart" | "table" | "kpi" | "nugget_feed" | "text";

export type AppItemReorderEntry = {
  id: number;
  item_type: AppItemType;
  tab_id: number | null;
};

export async function reorderAppItems(
  appId: number,
  items: AppItemReorderEntry[],
): Promise<void> {
  const res = await apiFetch(`/v1/apps/${appId}/items/reorder`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ items }),
  });
  if (!res.ok) {
    throw new Error(await readApiErrorMessage(res));
  }
}

/**
 * One tab's stored AI summary. `null` when it has not been generated yet.
 *
 * Named `AiSummary` because `AppTabSummary` in `appTabsApi` is the tab's *config* DTO — the two are
 * unrelated, and the App Studio panel imports both.
 */
export type AppTabAiSummary = {
  tab_id: number;
  summary: string | null;
};

export type AppItemsData = {
  charts: AppChartDataItem[];
  tables: AppTableDataItem[];
  kpis: AppKpiDataItem[];
  nugget_feeds: AppNuggetFeedDataItem[];
  text_widgets: AppTextWidgetDataItem[];
  /** Stored per-tab AI summaries. Read from storage — this endpoint never generates. */
  tab_summaries: AppTabAiSummary[];
  app_summary: string | null;
  /**
   * True when a `POST /summaries/refresh` would produce different text, because the widgets' data no
   * longer matches what the stored summaries were written from. Always false on a filtered refresh,
   * where the executed rows are a different dataset from the one the summaries describe.
   */
  summaries_stale: boolean;
};

function parseTabSummaries(value: unknown): AppTabAiSummary[] {
  if (!Array.isArray(value)) {
    return [];
  }
  const summaries: AppTabAiSummary[] = [];
  for (const raw of value) {
    if (typeof raw !== "object" || raw === null) {
      continue;
    }
    const row = raw as { tab_id?: unknown; summary?: unknown };
    if (typeof row.tab_id === "number") {
      summaries.push({
        tab_id: row.tab_id,
        summary: typeof row.summary === "string" ? row.summary : null,
      });
    }
  }
  return summaries;
}

export function parseAppItemsData(data: unknown): AppItemsData {
  if (typeof data !== "object" || data === null) {
    throw new Error("Unexpected response shape");
  }
  const body = data as {
    charts?: unknown;
    tables?: unknown;
    kpis?: unknown;
    nugget_feeds?: unknown;
    text_widgets?: unknown;
    tab_summaries?: unknown;
    app_summary?: unknown;
    summaries_stale?: unknown;
  };
  const charts = body.charts;
  const tables = body.tables;
  const kpis = body.kpis;
  const nuggetFeeds = body.nugget_feeds ?? [];
  const textWidgets = body.text_widgets ?? [];
  if (
    !Array.isArray(charts) ||
    !charts.every(isAppChartDataItem) ||
    !Array.isArray(tables) ||
    !tables.every(isAppTableDataItem) ||
    !Array.isArray(kpis) ||
    !kpis.every(isAppKpiDataItem) ||
    !Array.isArray(nuggetFeeds) ||
    !nuggetFeeds.every(isAppNuggetFeedDataItem) ||
    !Array.isArray(textWidgets) ||
    !textWidgets.every(isAppTextWidgetDataItem)
  ) {
    throw new Error("Unexpected response shape");
  }
  return {
    charts,
    tables,
    kpis,
    nugget_feeds: nuggetFeeds.map(normalizeAppNuggetFeedDataItem),
    text_widgets: textWidgets,
    // Tolerant like `nugget_feeds` above: the summary fields are additive, so a response without
    // them is still valid.
    tab_summaries: parseTabSummaries(body.tab_summaries),
    app_summary: typeof body.app_summary === "string" ? body.app_summary : null,
    summaries_stale: body.summaries_stale === true,
  };
}

/**
 * Unified builder refresh: executes every widget in one pass with cross-kind query
 * deduplication (a query reused by several widgets runs only once).
 */
export async function fetchAppItemsData(appId: number): Promise<AppItemsData> {
  const res = await apiFetch(`/v1/apps/${appId}/items/data`, { method: "GET" });
  if (!res.ok) {
    throw new Error(await readApiErrorMessage(res));
  }
  return parseAppItemsData(await res.json());
}

/**
 * Filtered refresh for one tab: executes only that tab's widgets with the given ephemeral tab
 * filters ANDed into each query. `tabId` null runs every widget (an app with no tabs). Results
 * are not persisted or cached; each widget carries `ignored_filters` for filters that did not
 * apply to it.
 */
export async function fetchAppItemsDataFiltered(
  appId: number,
  params: { tabId: number | null; filters: AppTabFilterRequest[] },
): Promise<AppItemsData> {
  const res = await apiFetch(`/v1/apps/${appId}/items/data`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ tab_id: params.tabId, filters: params.filters }),
  });
  if (!res.ok) {
    throw new Error(await readApiErrorMessage(res));
  }
  return parseAppItemsData(await res.json());
}

/** Which widget opens the drill-down, and what was clicked. */
export type DrilldownRequest = {
  ownerType: "kpi" | "table";
  ownerId: number;
  /** The clicked column. Omitted for a KPI owner, which has no column context. */
  triggerColumn?: string | null;
  /** The clicked row's value in the configured value column. Omitted for a KPI owner. */
  filterValue?: string | null;
  tabId: number | null;
  filters: AppTabFilterRequest[];
};

export type DrilldownData = {
  item_type: "chart" | "table";
  item: AppChartDataItem | AppTableDataItem;
};

export function parseDrilldownData(payload: unknown): DrilldownData {
  if (typeof payload !== "object" || payload === null) {
    throw new Error("Unexpected response shape");
  }
  const row = payload as Record<string, unknown>;
  const itemType = row.item_type;
  if (itemType === "chart" && isAppChartDataItem(row.item)) {
    return { item_type: "chart", item: row.item };
  }
  if (itemType === "table" && isAppTableDataItem(row.item)) {
    return { item_type: "table", item: row.item };
  }
  throw new Error("Unexpected response shape");
}

export function drilldownBody(request: DrilldownRequest): string {
  return JSON.stringify({
    owner_type: request.ownerType,
    owner_id: request.ownerId,
    trigger_column: request.triggerColumn ?? null,
    filter_value: request.filterValue ?? null,
    tab_id: request.tabId,
    filters: request.filters,
  });
}

/**
 * Execute one drill-down widget, filtered by the clicked cell.
 *
 * Separate from `fetchAppItemsDataFiltered` because that applies its filters to every widget on
 * the tab, whereas the cell's predicate must reach only the target. The tab's own active filters
 * are passed along so the dialog agrees with the tab it was opened from.
 */
export async function fetchAppDrilldownData(
  appId: number,
  request: DrilldownRequest,
): Promise<DrilldownData> {
  const res = await apiFetch(`/v1/apps/${appId}/items/drilldown/data`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: drilldownBody(request),
  });
  if (!res.ok) {
    throw new Error(await readApiErrorMessage(res));
  }
  return parseDrilldownData(await res.json());
}

/** Published-viewer twin of `fetchAppDrilldownData`. */
export async function fetchPublishedAppDrilldownData(
  appId: number,
  request: DrilldownRequest,
): Promise<DrilldownData> {
  const res = await apiFetch(`/v1/published-apps/${appId}/items/drilldown/data`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: drilldownBody(request),
  });
  if (!res.ok) {
    throw new Error(await readApiErrorMessage(res));
  }
  return parseDrilldownData(await res.json());
}

export type BatchQuerySpec = {
  query_id?: number | null;
  name?: string;
  sql?: string;
  input_mode?: AppWidgetInputMode;
  input_config?: AppQueryBuilderCompileRequest | null;
};

export type BatchWidgetSpec = {
  item_type: AppItemType;
  column_span?: number | null;
  tab_id?: number | null;
  // chart
  chart_definition?: GeneralizedChartDefinition;
  preferred_chart_type?: ChartType | null;
  // table
  description?: string | null;
  display_config?: AppTableDisplayConfig | null;
  // kpi
  name?: string;
  value_column?: string | null;
  format?: AppKpiFormat;
  /**
   * Drill-down target for a KPI: a chart/table that already exists on the same tab. Widgets
   * created by this same batch have no ids yet, so they cannot be targeted here.
   */
  drilldown_item_type?: AppKpiDrilldownItemType | null;
  drilldown_item_id?: number | null;
};

export type CreateWidgetsBatchPayload = {
  query: BatchQuerySpec;
  widgets: BatchWidgetSpec[];
};

export type CreateWidgetsBatchResponse = {
  query: AppQuerySummary;
  charts: AppChartSummary[];
  tables: AppTableSummary[];
  kpis: AppKpiSummary[];
};

export async function createWidgetsBatch(
  appId: number,
  payload: CreateWidgetsBatchPayload,
): Promise<CreateWidgetsBatchResponse> {
  const res = await apiFetch(`/v1/apps/${appId}/widgets/batch`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    throw new Error(await readApiErrorMessage(res));
  }
  const data: unknown = await res.json();
  if (typeof data !== "object" || data === null) {
    throw new Error("Unexpected response shape");
  }
  return data as CreateWidgetsBatchResponse;
}
